import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  InstallMobileIcon,
  IosShareIcon,
  AddBoxOutlinedIcon,
  MoreVertIcon,
} from "../components/icons";
import BottomSheet from "../components/BottomSheet";
import useInstallApp from "../hooks/useInstallApp";
import { trackInstallOffer, trackInstallResult } from "../ducks/tracking";
import { layoutOf } from "../utils/selfcheckoutMode";

/**
 * "Instalar o app" da loja, na tela de status do pedido. Aparece sempre, até
 * a loja estar instalada (utils/installApp.js). Aparelho da loja não oferece.
 */
export default function InstallAppCard() {
  const dispatch = useDispatch();
  const shop = useSelector((state) => state.shop);
  const { method, install } = useInstallApp();
  const [stepsMethod, setStepsMethod] = React.useState(null);

  const layout = layoutOf(shop);
  const visible = !!method && layout.mobileUi && !layout.sharedDevice;

  const offeredRef = React.useRef(false);
  React.useEffect(() => {
    if (!visible || offeredRef.current) return;
    offeredRef.current = true;
    dispatch(trackInstallOffer(method));
  }, [dispatch, visible, method]);

  if (!visible) return null;

  const onInstall = async () => {
    if (method !== "prompt") {
      setStepsMethod(method);
      return;
    }
    const outcome = await install();
    if (outcome) dispatch(trackInstallResult(method, outcome));
  };

  return (
    <section className="install-card" aria-labelledby="install-title">
      <span className="install-card__icon" aria-hidden="true">
        <InstallMobileIcon />
      </span>
      <div className="install-card__text">
        <h2 id="install-title" className="install-card__title">
          {`Tenha ${shop.name} no celular`}
        </h2>
        <p className="install-card__summary">
          Peça de novo com um toque, direto da tela inicial.
        </p>
        <button
          type="button"
          className="btn btn-shop install-card__button"
          onClick={onInstall}
        >
          {method === "prompt" ? "Instalar" : "Como instalar"}
        </button>
      </div>

      <BottomSheet
        show={!!stepsMethod}
        onHide={() => setStepsMethod(null)}
        title="Adicionar à tela inicial"
      >
        <InstallSteps method={stepsMethod} />
      </BottomSheet>
    </section>
  );
}

function InstallSteps({ method }) {
  if (method === "in-app") {
    return (
      <ol className="install-steps">
        <li>
          Toque no menu <MoreVertIcon fontSize="small" aria-hidden="true" /> ou
          em <strong>…</strong> no canto da tela.
        </li>
        <li>
          Escolha <strong>Abrir no navegador</strong> (Chrome ou Safari).
        </li>
        <li>
          Lá, toque em <strong>Instalar</strong> nesta tela.
        </li>
      </ol>
    );
  }

  if (method === "ios") {
    return (
      <ol className="install-steps">
        <li>
          Toque em <strong>Compartilhar</strong>{" "}
          <IosShareIcon fontSize="small" aria-label="ícone de compartilhar" />{" "}
          na barra do navegador.
        </li>
        <li>
          Role e escolha <strong>Adicionar à Tela de Início</strong>{" "}
          <AddBoxOutlinedIcon fontSize="small" aria-hidden="true" />.
        </li>
        <li>
          Toque em <strong>Adicionar</strong>. A loja abre direto no cardápio.
        </li>
      </ol>
    );
  }

  return (
    <ol className="install-steps">
      <li>
        Toque no menu do navegador{" "}
        <MoreVertIcon fontSize="small" aria-hidden="true" />.
      </li>
      <li>
        Escolha <strong>Instalar app</strong> ou{" "}
        <strong>Adicionar à tela inicial</strong>.
      </li>
      <li>Confirme. A loja abre direto no cardápio.</li>
    </ol>
  );
}
